/**
 * @file src/_modulos/prenota/tabela/layouts/config/hooks/useTableData.ts
 * @description Hook simplificado para dados da tabela
 *
 * Princípios aplicados:
 * - KISS: Lógica simples e direta
 * - SRP: Apenas gerencia dados da tabela
 * - YAGNI: Remove funcionalidades não utilizadas
 */

"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useCatalogs } from "store";
import { Filial } from "@apiUser";
import {
  usePrenotas,
  useInitFilials,
  useApiPayload,
  useTableStore,
} from "@PN/tabela";

/* -------------------------------------------------------------------------- */
/* HOOK PRINCIPAL                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Hook simplificado para dados da tabela
 *
 * Gerencia apenas o essencial: dados, loading e sincronização básica
 */
export function useTableData() {
  const {
    pageIndex,
    pageSize,
    searchTerm,
    selectedFilials: filials,
    setSelectedFilials: setFilials,
    setPagination,
  } = useTableStore();

  const hasInitialized = useRef(false);
  const [hasAttemptedSearch, setHasAttemptedSearch] = useState(false);

  // Obtém filiais acessíveis
  const { filiais: catalogFiliais, isLoading: isLoadingFiliais } =
    useCatalogs();
  const accessibleFilialCodes = useMemo(() => {
    return (catalogFiliais.all ?? []).map((f: Filial) => f.codigo);
  }, [catalogFiliais.all]);

  // Inicializa filiais se necessário
  useInitFilials(accessibleFilialCodes, filials, setFilials);

  // Constrói payload da API
  const apiPayload = useApiPayload({
    page: pageIndex + 1,
    pageSize,
    searchTerm,
    filiais: filials.length > 0 ? filials : accessibleFilialCodes,
  });

  // Carrega dados
  const { data, isLoading, isSuccess } = usePrenotas({
    ...apiPayload,
    filiais: filials.length > 0 ? filials : accessibleFilialCodes,
  });

  // Sincroniza paginação quando dados mudam
  useEffect(() => {
    if (!data || !isSuccess) return;

    setPagination({
      page: data.pagination.page,
      pageSize,
      totalCount: data.pagination.totalCount,
      totalPages: data.pagination.totalPages,
    });
  }, [data, pageSize, setPagination, isSuccess]);

  // Marca que houve tentativa de busca quando a query é executada
  useEffect(() => {
    if (isSuccess || (!isLoading && filials.length > 0)) {
      setHasAttemptedSearch(true);
    }
  }, [isSuccess, isLoading, filials.length]);

  // Inicializa filiais automaticamente (apenas uma vez)
  useEffect(() => {
    if (
      !hasInitialized.current &&
      filials.length === 0 &&
      accessibleFilialCodes.length > 0
    ) {
      hasInitialized.current = true;
      setFilials(accessibleFilialCodes);
    }
  }, [filials.length, accessibleFilialCodes, setFilials]);

  // Considera loading das filiais também
  const isActuallyLoading =
    isLoading ||
    isLoadingFiliais ||
    (filials.length === 0 && accessibleFilialCodes.length > 0) ||
    (!hasAttemptedSearch && filials.length > 0); // Se ainda não tentou buscar e tem filiais

  return {
    data,
    isLoading: isActuallyLoading,
    accessibleFilialCodes,
    hasAttemptedSearch,
  };
}
